import React, { useEffect, useState } from 'react';
import { Phone, PhoneIncoming, User, Clock, PhoneOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  collection, query, where, onSnapshot,
  doc, updateDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
 
interface IncomingCall {
  id: string;
  call_sid: string;
  from_number: string;
  to_number: string;
  caller_name: string | null;
  status: string;
  assigned_agent_id: string | null;
  created_at: string;
  metadata: Record<string, unknown>;
}
 
interface IncomingCallHandlerProps {
  className?: string;
  onAnswer?: (call: IncomingCall) => void;
  onDecline?: (call: IncomingCall) => void;
}
 
export function IncomingCallHandler({ className, onAnswer, onDecline }: IncomingCallHandlerProps) {
  const { user } = useAuth();
  const [incomingCalls, setIncomingCalls] = useState<IncomingCall[]>([]);
  const [ringDuration, setRingDuration] = useState<Record<string, number>>({});
 
  // → onSnapshot Firestore (realtime natif)
  useEffect(() => {
    if (!user?.id) return;
 
    const q = query(
      collection(db, 'incomingCalls'),
      where('status', '==', 'ringing')
    );
 
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const calls: IncomingCall[] = [];
      snapshot.forEach(d => {
        const data = d.data();
        const agentId = data.assigned_agent_id || data.assignedAgentId;
        // Filtrer : appels non assignés ou assignés à cet agent
        if (!agentId || agentId === user.id) {
          calls.push({
            id: d.id,
            call_sid: data.callSid || data.call_sid,
            from_number: data.fromNumber || data.from_number,
            to_number: data.toNumber || data.to_number,
            caller_name: data.callerName || data.caller_name || null,
            status: data.status,
            assigned_agent_id: agentId || null,
            created_at: data.createdAt?.toDate?.()?.toISOString() || data.created_at || new Date().toISOString(),
            metadata: data.metadata || {},
          });
        }
      });
 
      const prev = incomingCalls.length;
      setIncomingCalls(calls.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()));
 
      // Jouer la sonnerie si un nouvel appel arrive
      if (calls.length > prev) playRingtone();
    });
 
    return () => unsubscribe();
  }, [user?.id]);
 
  // Timer de sonnerie
  useEffect(() => {
    const interval = setInterval(() => {
      setRingDuration(prev => {
        const updated = { ...prev };
        incomingCalls.forEach(call => {
          if (call.status === 'ringing') {
            updated[call.id] = Math.floor((Date.now() - new Date(call.created_at).getTime()) / 1000);
          }
        });
        return updated;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [incomingCalls]);
 
  const playRingtone = () => {
    try {
      const audioContext = new AudioContext();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.value = 440;
      oscillator.type = 'sine';
      gainNode.gain.value = 0.3;
      oscillator.start();
      setTimeout(() => oscillator.stop(), 200);
      setTimeout(() => { oscillator.start(); setTimeout(() => oscillator.stop(), 200); }, 300);
    } catch (e) { console.log('Could not play notification sound'); }
  };
 
  const handleAnswer = async (call: IncomingCall) => {
    try {
      await updateDoc(doc(db, 'incomingCalls', call.id), {
        status: 'answered',
        answeredAt: serverTimestamp(),
        assignedAgentId: user?.id,
      });
      setIncomingCalls(prev => prev.filter(c => c.id !== call.id));
      toast.success('Appel accepté');
      onAnswer?.(call);
    } catch (error) {
      console.error('Error answering call:', error);
      toast.error("Erreur lors de la prise de l'appel");
    }
  };
 
  const handleDecline = async (call: IncomingCall) => {
    try {
      await updateDoc(doc(db, 'incomingCalls', call.id), { status: 'missed' });
      setIncomingCalls(prev => prev.filter(c => c.id !== call.id));
      onDecline?.(call);
    } catch (error) {
      console.error('Error declining call:', error);
    }
  };
 
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
 
  const formatPhoneNumber = (phone: string) => {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11 && cleaned.startsWith('1'))
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    return phone;
  };
 
  if (incomingCalls.length === 0) {
    return (
      <Card className={cn("w-full", className)}>
        <CardContent className="py-6 text-center text-muted-foreground">
          <PhoneIncoming className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">Aucun appel entrant</p>
          <p className="text-xs mt-1">Les appels entrants apparaîtront ici en temps réel</p>
        </CardContent>
      </Card>
    );
  }
 
  return (
    <div className={cn("space-y-3", className)}>
      {incomingCalls.map(call => (
        <Card key={call.id} className={cn("border-2 transition-all",
          call.status === 'ringing' && "border-green-500 animate-pulse bg-green-50/50 dark:bg-green-950/20")}>
          <CardContent className="py-4">
            <div className="flex items-center gap-4">
              <div className="relative">
                <Avatar className="h-12 w-12">
                  <AvatarFallback className="bg-green-100 text-green-700">
                    {call.caller_name?.[0] || <User className="h-5 w-5" />}
                  </AvatarFallback>
                </Avatar>
                {call.status === 'ringing' && (
                  <span className="absolute -top-1 -right-1 flex h-4 w-4">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-4 w-4 bg-green-500" />
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium truncate">{call.caller_name || 'Appelant inconnu'}</p>
                  {call.status === 'ringing' && <Badge variant="default" className="bg-green-600 animate-pulse">Entrant</Badge>}
                </div>
                <p className="text-sm text-muted-foreground">{formatPhoneNumber(call.from_number)}</p>
                <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                  <Clock className="h-3 w-3" />
                  <span>{formatDuration(ringDuration[call.id] || 0)}</span>
                </div>
              </div>
              {call.status === 'ringing' && (
                <div className="flex gap-2">
                  <Button onClick={() => handleDecline(call)} variant="destructive" size="icon" className="h-12 w-12 rounded-full">
                    <PhoneOff className="h-5 w-5" />
                  </Button>
                  <Button onClick={() => handleAnswer(call)} className="h-12 w-12 rounded-full bg-green-600 hover:bg-green-700" size="icon">
                    <Phone className="h-5 w-5" />
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
 